// Simple Quantity Restriction System - Product Page Only
class QuantityRestriction {
  constructor() {
    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupRestrictions());
    } else {
      this.setupRestrictions();
    }
  }

  setupRestrictions() {
    // Only work on product pages, not cart pages
    if (window.location.pathname.includes('/cart')) return;
    
    this.setupProductPage();
    this.interceptAddToCart();
  }

  setupProductPage() {
    const quantityInput = document.querySelector('input[name="quantity"]');
    const maxQuantityElement = document.querySelector('[data-max-quantity]');
    
    if (!quantityInput || !maxQuantityElement) return;
    
    const maxQuantity = parseInt(maxQuantityElement.dataset.maxQuantity);
    if (!maxQuantity) return;

    // Set HTML max attribute
    quantityInput.setAttribute('max', maxQuantity);
    
    // Add event listeners for input validation
    quantityInput.addEventListener('input', (e) => {
      this.validateQuantityInput(e.target, maxQuantity);
    });

    quantityInput.addEventListener('change', (e) => {
      this.validateQuantityInput(e.target, maxQuantity);
    });

    // Handle plus/minus buttons
    const increaseBtn = document.querySelector('button[name="plus"]');
    const decreaseBtn = document.querySelector('button[name="minus"]');
    
    if (increaseBtn) {
      increaseBtn.addEventListener('click', () => {
        setTimeout(() => {
          this.validateQuantityInput(quantityInput, maxQuantity);
          this.updateButtonStates(quantityInput, maxQuantity);
        }, 50);
      });
    }
    
    if (decreaseBtn) {
      decreaseBtn.addEventListener('click', () => {
        setTimeout(() => {
          this.updateButtonStates(quantityInput, maxQuantity);
        }, 50);
      });
    }

    // Initial button state
    this.updateButtonStates(quantityInput, maxQuantity);
  }

  validateQuantityInput(input, maxQuantity) {
    const value = parseInt(input.value);
    
    if (value > maxQuantity) {
      input.value = maxQuantity;
      this.showMessage(`Maximum ${maxQuantity} items allowed for this product`, 'warning');
    }
    
    if (value < 1) {
      input.value = 1;
    }
  }

  updateButtonStates(input, maxQuantity) {
    const value = parseInt(input.value);
    const plusBtn = document.querySelector('button[name="plus"]');
    const minusBtn = document.querySelector('button[name="minus"]');
    
    if (plusBtn) {
      if (value >= maxQuantity) {
        plusBtn.disabled = true;
        plusBtn.style.opacity = '0.5';
        plusBtn.style.cursor = 'not-allowed';
      } else {
        plusBtn.disabled = false;
        plusBtn.style.opacity = '1';
        plusBtn.style.cursor = 'pointer';
      }
    }
    
    if (minusBtn) {
      if (value <= 1) {
        minusBtn.disabled = true;
        minusBtn.style.opacity = '0.5';
        minusBtn.style.cursor = 'not-allowed';
      } else {
        minusBtn.disabled = false;
        minusBtn.style.opacity = '1';
        minusBtn.style.cursor = 'pointer';
      }
    }
  }

  interceptAddToCart() {
    // Intercept form submission
    const addToCartForms = document.querySelectorAll('form[action*="/cart/add"]');
    
    addToCartForms.forEach(form => {
      form.addEventListener('submit', async (e) => {
        const isValid = await this.validateAddToCart(form, e);
        if (!isValid) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
      });
    });
  }

  async validateAddToCart(form, event) {
    const formData = new FormData(form);
    const variantId = formData.get('id');
    const quantity = parseInt(formData.get('quantity') || 1);
    const maxQuantityElement = form.querySelector('[data-max-quantity]');
    
    if (!maxQuantityElement || !variantId) return true;
    
    const maxQuantity = parseInt(maxQuantityElement.dataset.maxQuantity);
    if (!maxQuantity) return true;

    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      
      const existingItem = cart.items.find(item => item.variant_id == variantId);
      const currentQuantity = existingItem ? existingItem.quantity : 0;
      const totalQuantity = currentQuantity + quantity;
      
      if (totalQuantity > maxQuantity) {
        const allowedQuantity = maxQuantity - currentQuantity;
        
        if (allowedQuantity <= 0) {
          this.showMessage(`Maximum ${maxQuantity} items already in cart for this product`, 'error');
          return false;
        } else {
          // Automatically adjust quantity to maximum allowed
          const quantityInput = form.querySelector('input[name="quantity"]');
          if (quantityInput) {
            quantityInput.value = allowedQuantity;
            this.showMessage(`Quantity adjusted to maximum allowed: ${allowedQuantity}`, 'warning');
            // Allow the form to submit with adjusted quantity
            return true;
          }
        }
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Error validating cart:', error);
      return true; // Allow if we can't validate
    }
  }

  showMessage(message, type = 'info') {
    // Remove existing notification
    const existing = document.getElementById('qty-restriction-message');
    if (existing) {
      existing.remove();
    }
    
    // Create new notification
    const notification = document.createElement('div');
    notification.id = 'qty-restriction-message';
    
    const colors = {
      error: { bg: '#e74c3c', border: '#c0392b' },
      warning: { bg: '#f39c12', border: '#d68910' },
      info: { bg: '#3498db', border: '#2980b9' }
    };
    
    const color = colors[type] || colors.info;
    
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${color.bg};
      color: white;
      padding: 12px 16px;
      border-radius: 6px;
      border-left: 4px solid ${color.border};
      z-index: 10000;
      font-size: 14px;
      font-weight: 500;
      max-width: 320px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.2);
      animation: slideIn 0.3s ease-out;
    `;
    
    // Add animation styles if not already added
    if (!document.getElementById('qty-restriction-styles')) {
      const styles = document.createElement('style');
      styles.id = 'qty-restriction-styles';
      styles.textContent = `
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes slideOut {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(100%); opacity: 0; }
        }
      `;
      document.head.appendChild(styles);
    }
    
    notification.textContent = message;
    document.body.appendChild(notification);
    
    // Auto-remove after 4 seconds
    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease-in';
      setTimeout(() => {
        if (notification.parentNode) {
          notification.remove();
        }
      }, 300);
    }, 4000);
  }
}

// Initialize the restriction system
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new QuantityRestriction();
  });
} else {
  new QuantityRestriction();
}